import React, { useCallback, useMemo, useState } from 'react'
import styled, { css, useTheme } from 'styled-components'
import TableCellInput from './TableCellInput'
import { GrowerDetail, ISellerTableProductItem } from '../../../../../modules/API/Seller/models/tableData'
import ChevronUpIcon from '../../../../../generic/icons/IconLib/ChevronUpIcon'
import PencilIcon from '../../../../../generic/icons/IconLib/PencilIcon'
import PlusIcon from '../../../../../generic/icons/IconLib/PlusIcon'
import TruckIcon from '../../../../../generic/icons/IconLib/TruckIcon'
import { ISellerChangeOrderProps } from '../types/handlerChangeOrder'
import SellerBodyBlockElement from './SellerBodyBlockElement'
import SellerBodyCell, { BalanceState } from './grid/SellerBodyCell'
import { FROZEN_COL0_WIDTH } from './grid/constants'

interface Props extends ISellerTableProductItem {
  item: ISellerTableProductItem
  handleChangeOrder: (props: ISellerChangeOrderProps) => void,
  openFreightCompanyModal: (id: string, growerID: number, customerID: number, freightCompanyID: number, qty: number, remark: string) => void
  openRemarkModal: (id: string, growerID: number, customerID: number, freightCompanyID: number, qty: number, remark: string, lineType?: number) => void
  handleSelectProduct: (item: ISellerTableProductItem, isEdit?: boolean) => void
  openTahazitRemarkModal: (id: string, growerID: number, customerID: number, qty: number, tahazitRemark: string) => void
}

const SellerBodyBlock = ({
  id,
  Growers,
  Customers,
  Product,
  handleChangeOrder,
  openFreightCompanyModal,
  openRemarkModal,
  handleSelectProduct,
  item,
  openTahazitRemarkModal
}: Props) => {
  const [isOpenBlock, setIsOpenBlock] = useState(false)
  const theme = useTheme()

  const handleBlockClick = () => {
    setIsOpenBlock(!isOpenBlock)
  }
  const handleSelectProductClick = (e: React.MouseEvent<HTMLElement>, isEdit?: boolean) => {
    e.stopPropagation()
    handleSelectProduct(item, isEdit)
  }
  const handleTopOrderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, customer: number, freightCompanyID: number) => {
    handleChangeOrder({
      id: id || '',
      qty: +e.target.value,
      customerTopID: customer,
      FreightCompanyID: freightCompanyID
    })
  }, [handleChangeOrder, id])

  const handleInnerOrderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, grower: number, customer: number, freightCompanyID: number) => {
    handleChangeOrder({
      id: id || '',
      qty: +e.target.value,
      growerID: grower,
      customerInnerID: customer,
      FreightCompanyID: freightCompanyID
    })
  }, [handleChangeOrder, id])

  const handleInputForecastChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, growerID: number) => {
    handleChangeOrder({
      id: id || '',
      growerID: growerID,
      qty: +e.target.value,
      isForecast: true
    })
  }, [handleChangeOrder, id])

  const handleSelectFreightCompany = useCallback((e: React.MouseEvent, growerID: number, customerID: number, freightCompanyID: number, qty: number, remark: string) => {
    openFreightCompanyModal(id || '', growerID, customerID, freightCompanyID, qty, remark)
  }, [openFreightCompanyModal, id])

  const handleOpenRemarkModal = useCallback((e: React.MouseEvent, growerID: number, customerID: number, freightCompanyID: number, qty: number, remark: string, lineType?: number) => {
    openRemarkModal(id || '', growerID, customerID, freightCompanyID, qty, remark, lineType)
  }, [openRemarkModal, id])

  const culcTotalByTopCustomers = useMemo(() => {
    return Customers.reduce((acc, cur) => {
      // Суммируем количество паллет на верхнем уровне клиента
      let topLevelPallets = cur.OrderDetail.Order_Pallets;

      // Добавляем количество паллет от всех производителей для этого клиента
      const growerPallets = Growers.reduce((growerAcc, grower) => {
        const customerInGrower = grower.Customers.find(c => c.Customer.CustomerID === cur.Customer.CustomerID);
        return growerAcc + (customerInGrower?.OrderDetail.Order_Pallets || 0);
      }, 0);

      // Возвращаем максимальное значение между верхним уровнем и суммой от производителей
      return acc + Math.max(topLevelPallets, growerPallets);
    }, 0)
  }, [Customers, Growers])

  const culcTotalByGrowers = useCallback((grower: GrowerDetail) => {
    return grower.Customers.reduce((acc, cur) => {
      return acc + cur.OrderDetail.Order_Pallets
    }, 0)
  }, [])

  const culcTotalTahazit = useMemo(() => {
    return Growers.reduce((acc, cur) => {
      return acc + cur.Forecast.ForecastPallets
    }, 0)
  }, [Growers])

  const isAllowToEdit = useMemo(() => {
    return item.Growers.some(grower => grower.Order_Pallets !== 0 )
  }, [item])

  const balanceState: BalanceState =
  culcTotalByTopCustomers === 0 && culcTotalTahazit === 0 ? undefined :
  culcTotalByTopCustomers - culcTotalTahazit === 0 ? undefined
    :  culcTotalTahazit - culcTotalByTopCustomers < 0 ? 'under'
    : 'over'

  const TahazitRestAndColor = useCallback((grower: GrowerDetail) => {
    //calculate
    const rest = grower.Forecast.ForecastPallets - culcTotalByGrowers(grower)
    const state: BalanceState = rest === 0 ? undefined : rest < 0 ? 'under' : 'over'

    return {
      rest,
      state
    }
  }, [culcTotalByGrowers])

  return (
    <React.Fragment>
      <tr>
        <SellerBodyCell
          onClick={handleBlockClick}
          sticky
          offset={0}
          width={`${FROZEN_COL0_WIDTH}px`}
        >
          <NameCell>
            <span>{`${Product.ProductDesc.trim()} ${Product.VarietyDesc.trim()} ${Product.SizeID.trim()}`}</span>
            <IconsContainer>
              {
                isAllowToEdit && (
                  <IconBtn
                    onClick={(e) => handleSelectProductClick(e, true)}
                  >
                    <PencilIcon size={16} />
                  </IconBtn>
                )
              }
              {
                item.Growers.length > 0 && (
                  <IconBtn $open={isOpenBlock}>
                    <ChevronUpIcon size={16} />
                  </IconBtn>
                )
              }
              <IconBtn
                onClick={handleSelectProductClick}
              >
                <PlusIcon size={16} />
              </IconBtn>
            </IconsContainer>
          </NameCell>
        </SellerBodyCell>
        <SellerBodyCell
          sticky
          offset={FROZEN_COL0_WIDTH}
          lastFrozen
          width='100px'
        >
          {culcTotalTahazit || ""}
        </SellerBodyCell>
        <SellerBodyCell
          state={balanceState}
        >
          {culcTotalByTopCustomers || ""}
        </SellerBodyCell>
        {
          Customers.map((customer, index) => (
            <SellerBodyBlockElement
              key={customer.Customer.CustomerID}
              Growers={Growers}
              {...customer}
              index={index}
              handleOpenRemarkModal={handleOpenRemarkModal}
              handleTopOrderChange={(e) => handleTopOrderChange(e, customer.Customer.CustomerID, customer.OrderDetail.FreightCompanyID)}
            />
          ))
        }
      </tr>
      {
        isOpenBlock &&
        Growers.map((grower) => {
          const { rest, state } = TahazitRestAndColor(grower)
          return (
            <tr
              key={grower.Grower.GrowerID}
            >
              <SellerBodyCell
                sticky
                offset={0}
                tint
                width={`${FROZEN_COL0_WIDTH}px`}
              >
                {grower.Grower.GrowerName}
              </SellerBodyCell>
              <SellerBodyCell
                sticky
                offset={FROZEN_COL0_WIDTH}
                lastFrozen
                state={state}
                tint
                width='100px'
              >
                <CellContent>
                  <TableCellInput
                    value={grower.Forecast.ForecastPallets}
                    onChange={(e) =>
                      handleInputForecastChange(e, grower.Grower.GrowerID)
                    }
                  />
                  {
                    grower.Forecast.RecID !== 0 && (
                      <IconBtn
                        $marked={grower.Forecast.Comments.trim() !== ""}
                        onClick={(e) => openTahazitRemarkModal(id || '', grower.Grower.GrowerID, 0, 0, grower.Forecast.Comments)}
                      >
                        <PencilIcon size={16} />
                      </IconBtn>
                    )
                  }
                  <RestText>
                    {rest === 0 ? "" : `(${rest})`}
                  </RestText>
                </CellContent>
              </SellerBodyCell>
              <SellerBodyCell tint>
                {culcTotalByGrowers(grower) || ""}
              </SellerBodyCell>
              {
                grower.Customers.map((customer) => (
                  <SellerBodyCell
                    key={customer.Customer.CustomerID}
                    tint
                  >
                    <CellContent>
                      <TableCellInput
                        value={customer.OrderDetail.Order_Pallets}
                        onChange={(e) => handleInnerOrderChange(e, grower.Grower.GrowerID, customer.Customer.CustomerID, customer.OrderDetail.FreightCompanyID)}
                      />
                      {
                        customer.OrderDetail.RecID !== 0 && (
                          <IconBtn
                            onClick={(e) => handleSelectFreightCompany(e, grower.Grower.GrowerID, customer.Customer.CustomerID, customer.OrderDetail.FreightCompanyID, customer.OrderDetail.Order_Pallets, customer.OrderDetail.Remarks)}
                          >
                            <TruckIcon
                              size={16}
                              color={customer.OrderDetail.FreightCompanyID !== 0 ? theme.colors.success : theme.colors.danger}
                            />
                          </IconBtn>
                        )
                      }
                      {
                        customer.OrderDetail.RecID !== 0 && (
                          <IconBtn
                            title={customer.OrderDetail.Remarks.trim()}
                            $marked={customer.OrderDetail.Remarks.trim() === ""}
                            onClick={(e) => handleOpenRemarkModal(e, grower.Grower.GrowerID, customer.Customer.CustomerID, customer.OrderDetail.FreightCompanyID, customer.OrderDetail.Order_Pallets, customer.OrderDetail.Remarks)}
                          >
                            <PencilIcon size={16} />
                          </IconBtn>
                        )
                      }
                    </CellContent>
                  </SellerBodyCell>
                ))
              }
            </tr>
          )

        })
      }
    </React.Fragment>
  )
}

const NameCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5em;
`
const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
`
const CellContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25em;
`
const RestText = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.sm};
  color: ${({ theme }) => theme.colors.ink3};
`
const IconBtn = styled.span<{ $marked?: boolean; $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  min-height: 16px;
  cursor: pointer;
  color: ${({ theme, $marked }) => ($marked ? theme.colors.danger : theme.colors.icon)};
  transition:
    transform 0.25s ease,
    color ${({ theme }) => theme.motion.fast} ease;
  ${({ $open }) =>
    $open !== undefined &&
    css`
      transform: rotate(${$open ? '0deg' : '180deg'});
    `}
  &:hover {
    color: ${({ theme }) => theme.colors.ink};
  }
`
export default SellerBodyBlock
